import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  StopCircleOutlined,
  FiberManualRecord,
  PeopleOutlined,
  TrendingUpOutlined,
  HelpOutlineOutlined,
  AccessTimeOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import { getInitials } from '../../utils/getInitials';

// --- Types ---

interface SessionStudent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  joinedAt: string;
  progress: { completedSteps: number; totalSteps: number; percent: number };
}

interface SessionHelpRequest {
  id: string;
  studentName: string;
  message: string;
  status: string;
  priority: string;
  createdAt: string;
  resolvedBy: string | null;
}

interface TimelineEntry {
  event: string;
  timestamp: string;
  details?: string;
}

interface SessionDetail {
  id: string;
  name: string;
  status: string;
  scheduledStart: string;
  actualStart: string | null;
  actualEnd: string | null;
  duration: number;
  maxStudents: number;
  createdAt: string;
  workshop: { id: string; title: string; stepCount: number };
  instructor: { id: string; firstName: string; lastName: string; email: string };
  coInstructors?: Array<{ instructorId: string; firstName: string; lastName: string; email: string }>;
  students: SessionStudent[];
  helpRequests: SessionHelpRequest[];
  timeline: TimelineEntry[];
}

// --- Status chip maps ---

const sessionStatusColor: Record<string, 'info' | 'success' | 'warning' | 'default' | 'error'> = {
  scheduled: 'info',
  active: 'success',
  paused: 'warning',
  completed: 'default',
  cancelled: 'error',
};

const helpStatusColor: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  closed: 'default',
};

const helpPriorityColor: Record<string, 'default' | 'warning' | 'error'> = {
  normal: 'default',
  high: 'warning',
  urgent: 'error',
};

const timelineEventColor: Record<string, string> = {
  created: '#3b82f6',
  started: '#22c55e',
  'Student joined': '#8b5cf6',
  'Help request': '#f59e0b',
  ended: '#ef4444',
};

// --- Helpers ---

function formatElapsed(startIso: string): string {
  const diff = dayjs().diff(dayjs(startIso), 'minute');
  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;
  return `${hours}h ${minutes}m`;
}

// --- Component ---

const AdminSessionDetail: React.FC = () => {
  const { showNotification } = useNotification();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tabIndex, setTabIndex] = useState(0);

  const [forceEndOpen, setForceEndOpen] = useState(false);
  const [forceEndReason, setForceEndReason] = useState('');
  const [forceEndLoading, setForceEndLoading] = useState(false);

  const fetchSession = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await adminService.getSession(id);
      setSession(res.data);
    } catch {
      showNotification('Failed to load session details', 'error');
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const averageProgress = useMemo(() => {
    if (!session || session.students.length === 0) return 0;
    const total = session.students.reduce((sum, s) => sum + s.progress.percent, 0);
    return Math.round(total / session.students.length);
  }, [session]);

  const handleForceEnd = async () => {
    if (!id) return;
    setForceEndLoading(true);
    try {
      await adminService.forceEndSession(id, forceEndReason);
      setForceEndOpen(false);
      setForceEndReason('');
      fetchSession();
    } catch {
      showNotification('Failed to force-end session', 'error');
    } finally {
      setForceEndLoading(false);
    }
  };

  // --- Loading / Error ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !session) {
    return <Alert severity="error">{error || 'Session not found'}</Alert>;
  }

  const isActiveOrPaused = session.status === 'active' || session.status === 'paused';
  const showStats = session.status === 'active' || session.status === 'completed';
  const instructorInitials = getInitials(session.instructor.firstName, session.instructor.lastName);

  // --- Render ---

  return (
    <Box>
      {/* Back button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/sessions')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to Sessions
      </Button>

      {/* Top section — Session info + Actions */}
      <Paper sx={{ p: 3, borderRadius: '12px', mb: 3 }} elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          {/* Left side */}
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {session.name}
              </Typography>
              <Chip
                label={session.status}
                size="small"
                color={sessionStatusColor[session.status] || 'default'}
                sx={{ textTransform: 'capitalize', fontWeight: 500 }}
              />
            </Box>

            {/* Tech Eval link */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Tech Eval</Typography>
              <Box>
                <Chip
                  label={session.workshop.title}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => navigate(`/admin/workshops/${session.workshop.id}`)}
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Box>

            {/* Instructor */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#8b5cf6',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/admin/users/${session.instructor.id}`)}
              >
                {instructorInitials}
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate(`/admin/users/${session.instructor.id}`)}
                >
                  {session.instructor.firstName} {session.instructor.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">Instructor</Typography>
              </Box>
            </Box>

            {/* Co-Instructors */}
            {session.coInstructors && session.coInstructors.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Co-Instructors</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {session.coInstructors.map((ci) => (
                    <Chip
                      key={ci.instructorId}
                      label={`${ci.firstName} ${ci.lastName}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Key info */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">Scheduled Start</Typography>
                <Typography variant="body2">{dayjs(session.scheduledStart).format('MMM D, YYYY h:mm A')}</Typography>
              </Grid>
              {session.actualStart && (
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">Actual Start</Typography>
                  <Typography variant="body2">{dayjs(session.actualStart).format('MMM D, YYYY h:mm A')}</Typography>
                </Grid>
              )}
              {session.actualEnd && (
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">Ended</Typography>
                  <Typography variant="body2">{dayjs(session.actualEnd).format('MMM D, YYYY h:mm A')}</Typography>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2">{session.duration} min</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">Max Students</Typography>
                <Typography variant="body2">{session.maxStudents}</Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Right side — Actions */}
          {isActiveOrPaused && (
            <Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopCircleOutlined />}
                onClick={() => { setForceEndReason(''); setForceEndOpen(true); }}
                sx={{ textTransform: 'none' }}
              >
                Force End
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Stats row */}
      {showStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }} elevation={0}>
              <PeopleOutlined sx={{ fontSize: 28, color: '#3b82f6', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                {session.students.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">Students Enrolled</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }} elevation={0}>
              <TrendingUpOutlined sx={{ fontSize: 28, color: '#22c55e', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#22c55e' }}>
                {averageProgress}%
              </Typography>
              <Typography variant="caption" color="text.secondary">Average Progress</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }} elevation={0}>
              <HelpOutlineOutlined sx={{ fontSize: 28, color: '#f59e0b', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                {session.helpRequests.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">Help Requests</Typography>
            </Paper>
          </Grid>
          {session.status === 'active' && session.actualStart && (
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper sx={{ p: 2.5, borderRadius: '12px', textAlign: 'center' }} elevation={0}>
                <AccessTimeOutlined sx={{ fontSize: 28, color: '#8b5cf6', mb: 0.5 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                  {formatElapsed(session.actualStart)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Elapsed Time</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }} elevation={0}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" sx={{ textTransform: 'none', fontWeight: 500 }} />
          <Tab label="Students" sx={{ textTransform: 'none', fontWeight: 500 }} />
          <Tab label="Help Requests" sx={{ textTransform: 'none', fontWeight: 500 }} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Overview (Timeline) */}
          {tabIndex === 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Session Timeline
              </Typography>
              {session.timeline.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No timeline events yet</Typography>
              ) : (
                <Box sx={{ position: 'relative', pl: 3 }}>
                  {/* Vertical line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 7,
                      top: 4,
                      bottom: 4,
                      width: 2,
                      bgcolor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                  {session.timeline.map((entry, i) => {
                    const dotColor = timelineEventColor[entry.event] || '#9e9e9e';
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5, position: 'relative' }}>
                        {/* Dot */}
                        <FiberManualRecord
                          sx={{
                            fontSize: 14,
                            color: dotColor,
                            position: 'absolute',
                            left: -25,
                            top: 3,
                          }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {entry.event}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(entry.timestamp).fromNow()}
                            {' \u2014 '}
                            {dayjs(entry.timestamp).format('MMM D, YYYY h:mm A')}
                          </Typography>
                          {entry.details && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {entry.details}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* Tab 1: Students */}
          {tabIndex === 1 && (
            <Box>
              {session.students.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No students enrolled yet
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Progress</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...session.students]
                        .sort((a, b) => b.progress.percent - a.progress.percent)
                        .map((student) => {
                          const initials = getInitials(student.firstName, student.lastName);
                          return (
                            <TableRow key={student.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#3b82f6', fontSize: '0.7rem', fontWeight: 600 }}>
                                    {initials}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {student.firstName} {student.lastName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">{student.email}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {dayjs(student.joinedAt).fromNow()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={student.progress.percent}
                                    sx={{
                                      flex: 1,
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: 'divider',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        bgcolor: student.progress.percent === 100 ? '#22c55e' : '#3b82f6',
                                      },
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {student.progress.completedSteps}/{student.progress.totalSteps} steps
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tab 2: Help Requests */}
          {tabIndex === 2 && (
            <Box>
              {session.helpRequests.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No help requests
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Resolved By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...session.helpRequests]
                        .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
                        .map((hr) => (
                          <TableRow key={hr.id} hover>
                            <TableCell>
                              <Typography variant="body2">{hr.studentName}</Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 250 }}>
                              <Typography variant="body2" noWrap title={hr.message}>
                                {hr.message}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={hr.priority}
                                size="small"
                                color={helpPriorityColor[hr.priority] || 'default'}
                                sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={hr.status.replace('_', ' ')}
                                size="small"
                                color={helpStatusColor[hr.status] || 'default'}
                                sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {dayjs(hr.createdAt).fromNow()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {hr.resolvedBy || '\u2014'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Force End Dialog */}
      <Dialog
        open={forceEndOpen}
        onClose={() => setForceEndOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>Force End Session</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately end the session for all {session.students.length} students.
          </Alert>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            value={forceEndReason}
            onChange={(e) => setForceEndReason(e.target.value)}
            placeholder="Provide a reason for force-ending this session..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setForceEndOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceEnd}
            disabled={forceEndLoading || !forceEndReason.trim()}
          >
            {forceEndLoading ? <CircularProgress size={20} /> : 'Force End Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSessionDetail;
